import moment from 'moment';
import { useRouter } from 'next/router';
import React, { createContext, useContext, useRef, useState } from 'react';
import { TenancyTypeOption } from '~graphql/__generated__/globalTypes';
import { useTranslation } from '~i18n';

interface IDate {
  startDate: any;
  endDate: any;
}

export interface FlexibleTenancyInfo {
  tenancyOptionId: string;
  startDate: string;
  endDate: string;
  selectableStartDates: string[];
}
export interface ISelectedBedProps {
  bedName: string | undefined | null;
  bedId: string | undefined | null;
  totalPriceOriginal: number | undefined | null;
  priceForBillingCycle: number | undefined | null;
  pricePerNight: number | undefined | null;
  pricePerNightOriginal: number | undefined | null;
  marketingPrice?: number | undefined | null;
  marketingInstallmentNumber?: number | undefined | null;
  pricePerMonth: number | undefined | null;
  pricePerMonthOriginal: number | undefined | null;
}

export const initialSelectedFlat = {
  bedName: '',
  bedId: '',
  totalPriceOriginal: 0,
  priceForBillingCycle: 0,
  pricePerNight: 0,
  pricePerNightOriginal: 0,
  marketingPrice: 0,
  marketingInstallmentNumber: 0,
  pricePerMonth: 0,
  pricePerMonthOriginal: 0,
};

export const defaultEditBookingInput = {
  academicYearId: '',
  tenancyOptionId: '',
  tenancyStartDate: '',
  tenancyEndDate: '',
  roomTypeId: '',
};

interface IBookingProcessSelectBedProps {
  selectedFlat: ISelectedBedProps;
  setSelectedFlat: (value: ISelectedBedProps) => void;

  selectBedRoomTypeId: string;
  setSelectBedRoomTypeId: (id: string) => void;

  selectBedTenancyOptionId: string | null;
  setSelectBedTenancyOptionId: (id: string) => void;

  selectBedAcademicYearId: string;
  setSelectBedAcademicYearId: (id: string) => void;

  selectBedTenancyDate: IDate;
  setSelectBedTenancyDate: (date: IDate) => void;

  selectTenancyType: TenancyTypeOption | null;
  setSelectTenancyType: (type: TenancyTypeOption | null) => void;

  selectableEndDates: string[] | undefined;
  setSelectableEndDates: (dates: string[] | undefined) => void;

  flexibleTenancyInfo: FlexibleTenancyInfo | null;
  setFlexibleTenancyInfo: (info: FlexibleTenancyInfo | null) => void;

  initEditBookingInput: any;

  handleInitTenancyOptionStates: () => void;
}

const BookingProcessSelectBedContext = createContext<IBookingProcessSelectBedProps>({
  selectedFlat: initialSelectedFlat,
  setSelectedFlat: () => {},

  selectBedRoomTypeId: '',
  setSelectBedRoomTypeId: () => {},

  selectBedTenancyOptionId: '',
  setSelectBedTenancyOptionId: () => {},

  selectBedAcademicYearId: '',
  setSelectBedAcademicYearId: () => {},

  selectBedTenancyDate: {
    startDate: '',
    endDate: '',
  },
  setSelectBedTenancyDate: () => {},

  selectTenancyType: null,
  setSelectTenancyType: () => {},

  selectableEndDates: undefined,
  setSelectableEndDates: () => {},

  flexibleTenancyInfo: null,
  setFlexibleTenancyInfo: () => {},

  initEditBookingInput: {},

  handleInitTenancyOptionStates: () => {},
});

export const BookingProcessSelectBedProvider: React.FC = ({ children }) => {
  const { t } = useTranslation(['student_portal']);
  const router = useRouter();

  const {
    initialRoomTypeId,
    initialTenancyOptionId,
    initialAcademicYearId,
    initialTenancyEndDate,
    initialTenancyStartDate,
  } = router.query;

  const initEditBookingInput = useRef({
    ...defaultEditBookingInput,
  });

  const [selectedFlat, setSelectedFlat] = useState<ISelectedBedProps>(initialSelectedFlat);

  const [selectBedRoomTypeId, setSelectBedRoomTypeId] = useState(
    initialRoomTypeId ? initialRoomTypeId.toString() : ''
  );

  const [selectBedTenancyOptionId, setSelectBedTenancyOptionId] = useState(
    initialTenancyOptionId ? initialTenancyOptionId.toString() : null
  );
  const [selectBedAcademicYearId, setSelectBedAcademicYearId] = useState(
    initialAcademicYearId ? initialAcademicYearId.toString() : ''
  );

  const [selectBedTenancyDate, setSelectBedTenancyDate] = useState<IDate>({
    startDate: initialTenancyStartDate ? moment(initialTenancyStartDate) : '',
    endDate: initialTenancyEndDate ? moment(initialTenancyEndDate) : '',
  });

  //for tenancy option date range picker start
  const [selectTenancyType, setSelectTenancyType] = useState<TenancyTypeOption | null>(null);

  const [selectableEndDates, setSelectableEndDates] = useState<string[] | undefined>(undefined);

  const [flexibleTenancyInfo, setFlexibleTenancyInfo] = useState<FlexibleTenancyInfo | null>(null);

  //for tenancy option date range picker end

  const handleInitTenancyOptionStates = () => {
    setSelectTenancyType(null);
    setSelectableEndDates(undefined);
    setFlexibleTenancyInfo(null);
    setSelectBedTenancyOptionId(null);
    setSelectBedAcademicYearId('');
    setSelectBedTenancyDate({
      startDate: '',
      endDate: '',
    });
  };

  return (
    <BookingProcessSelectBedContext.Provider
      value={{
        selectedFlat,
        setSelectedFlat,

        selectBedRoomTypeId,
        setSelectBedRoomTypeId,

        selectBedTenancyOptionId,
        setSelectBedTenancyOptionId,

        selectBedAcademicYearId,
        setSelectBedAcademicYearId,

        selectBedTenancyDate,
        setSelectBedTenancyDate,

        selectTenancyType,
        setSelectTenancyType,

        selectableEndDates,
        setSelectableEndDates,

        flexibleTenancyInfo,
        setFlexibleTenancyInfo,

        initEditBookingInput,

        handleInitTenancyOptionStates,
      }}
    >
      {children}
    </BookingProcessSelectBedContext.Provider>
  );
};

export const useBookingProcessSelectBedContenxt = () => useContext(BookingProcessSelectBedContext);
